import type { CodeSent, SignInMethods } from '@gradex-hire/contracts';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '../../app/layouts/AuthLayout.js';
import { currentLanguage } from '../../lib/i18n/index.js';
import { PageHeader } from '../../ui/components/PageHeader.js';
import { CALLING_CODES, DEFAULT_CALLING_CODE, toE164 } from '../../ui/components/phone.js';
import { NoCodeHelp } from '../../ui/components/NoCodeHelp.js';
import {
  afterSignIn,
  browserTimeZone,
  consumeCode,
  getMethods,
  preloadClient,
  requestCode,
  resendCode,
} from './api.js';
import {
  clearAttempt,
  loadAttempt,
  maskContact,
  saveAttempt,
  type SignInAttempt,
} from './attempt.js';
import { CodeEntry } from './CodeEntry.js';
import { errorCode, SignedIn } from './parts.js';
import '../../ui/components/button.css';
import '../../ui/components/field.css';
import './sign-in.css';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** What the code step shows: the attempt, and the full address while this tab knows it. */
interface CodeStep {
  attempt: SignInAttempt;
  display: string;
}

/**
 * The sign-in page (`/sign-in?next=…`): an email address or, while phone
 * sign-in is on, a phone number; then the six-digit code (or the one-tap
 * link in the email). On success a sage tick, then a view transition to the
 * account's area (or `next`).
 *
 * The confirmation never says whether the address has an account.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search: { next?: unknown } = useSearch({ strict: false });
  const next = typeof search.next === 'string' ? search.next : undefined;
  const [methods, setMethods] = useState<SignInMethods>({ email: true, phone: false });
  const [step, setStep] = useState<CodeStep | undefined>(() => {
    const saved = loadAttempt();
    return saved ? { attempt: saved, display: saved.contact } : undefined;
  });
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    getMethods().then(setMethods, () => undefined);
  }, []);

  const finish = async (account: Parameters<typeof afterSignIn>[0]) => {
    clearAttempt();
    setSignedIn(true);
    const destination = await afterSignIn(account, step?.attempt.next ?? next);
    // Let the tick draw before moving on.
    window.setTimeout(() => {
      void navigate({ to: destination, viewTransition: true });
    }, 650);
  };

  return (
    <AuthLayout>
      <div className="sign-in">
        {signedIn ? null : (
          <PageHeader
            title={t('signIn.title')}
            {...(step ? {} : { description: t('signIn.description') })}
          />
        )}
        {signedIn ? (
          <SignedIn title={t('signIn.done.title')} body={t('signIn.done.body')} />
        ) : step ? (
          <CodeForm
            step={step}
            onRestart={() => {
              clearAttempt();
              setStep(undefined);
            }}
            onResent={(attempt) => {
              setStep({ ...step, attempt });
            }}
            onSignedIn={finish}
          />
        ) : (
          <ContactForm
            phone={methods.phone}
            next={next}
            onSent={(attempt, display) => {
              setStep({ attempt, display });
            }}
          />
        )}
      </div>
    </AuthLayout>
  );
}

function attemptFrom(
  sent: CodeSent,
  method: 'EMAIL' | 'SMS',
  contact: string,
  next: string | undefined,
): SignInAttempt {
  return {
    deviceId: sent.deviceId,
    preAuthSessionId: sent.preAuthSessionId,
    method,
    contact: maskContact(method, contact),
    sentAt: Date.now(),
    codeLifetimeSeconds: sent.codeLifetimeSeconds,
    resendAfterSeconds: sent.resendAfterSeconds,
    ...(next ? { next } : {}),
  };
}

function ContactForm({
  phone,
  next,
  onSent,
}: {
  phone: boolean;
  next: string | undefined;
  onSent: (attempt: SignInAttempt, display: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [method, setMethod] = useState<'EMAIL' | 'SMS'>('EMAIL');
  const [email, setEmail] = useState('');
  const [region, setRegion] = useState(DEFAULT_CALLING_CODE.region);
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const regionNames = new Intl.DisplayNames([i18n.language], { type: 'region' });
  const countries = CALLING_CODES.filter(
    (country, index) =>
      CALLING_CODES.findIndex((other) => other.region === country.region) === index,
  );

  const submit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setError(undefined);
    let contact: string | undefined;
    if (method === 'EMAIL') {
      contact = email.trim();
      if (!EMAIL.test(contact)) {
        setError(t('signIn.contact.invalidEmail'));
        return;
      }
    } else {
      const country = countries.find((entry) => entry.region === region) ?? DEFAULT_CALLING_CODE;
      contact = toE164(country, number);
      if (!contact) {
        setError(t('signIn.contact.invalidPhone'));
        return;
      }
    }
    setPending(true);
    try {
      const locale = currentLanguage();
      const sent = await requestCode(
        method === 'EMAIL'
          ? { method, email: contact, locale }
          : { method, phoneNumber: contact, locale },
      );
      const attempt = attemptFrom(sent, method, contact, next);
      saveAttempt(attempt);
      onSent(attempt, contact);
    } catch (failure) {
      setError(t(`signIn.errors.${errorCode(failure, ['rate_limited', 'method_unavailable'])}`));
    } finally {
      setPending(false);
    }
  };

  const errorId = `${id}-error`;
  return (
    <form className="sign-in__form" noValidate onSubmit={(event) => void submit(event)}>
      {phone ? (
        <div className="segmented" role="group" aria-label={t('signIn.contact.method')}>
          {(['EMAIL', 'SMS'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="segmented__option"
              aria-pressed={method === option}
              onClick={() => {
                setMethod(option);
                setError(undefined);
              }}
            >
              {option === 'EMAIL' ? t('signIn.contact.email') : t('signIn.contact.phone')}
            </button>
          ))}
        </div>
      ) : null}

      {method === 'EMAIL' ? (
        <div className="field" data-invalid={error ? true : undefined}>
          <label className="field__label" htmlFor={`${id}-email`}>
            {t('signIn.contact.emailLabel')}
          </label>
          <input
            id={`${id}-email`}
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            onFocus={preloadClient}
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>
      ) : (
        <div className="field" data-invalid={error ? true : undefined}>
          <span className="field__label" id={`${id}-phone-label`}>
            {t('signIn.contact.phoneLabel')}
          </span>
          <div className="phone-input">
            <select
              className="input phone-input__country"
              aria-label={t('signIn.contact.country')}
              value={region}
              onChange={(event) => {
                setRegion(event.target.value);
              }}
            >
              {countries.map((country) => (
                <option key={country.region} value={country.region}>
                  {`${regionNames.of(country.region) ?? country.region} +${country.code}`}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              onFocus={preloadClient}
              required
              value={number}
              aria-labelledby={`${id}-phone-label`}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(event) => {
                setNumber(event.target.value);
              }}
            />
          </div>
        </div>
      )}

      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}

      <button
        className="btn btn--primary sign-in__submit"
        type="submit"
        disabled={pending}
        aria-busy={pending}
        onPointerDown={(event) => {
          // Keep an already-open phone keyboard through the request, so
          // Safari can transfer it to the code input when the response arrives.
          if (document.activeElement?.tagName === 'INPUT') event.preventDefault();
        }}
      >
        {pending ? t('signIn.contact.sending') : t('signIn.contact.send')}
      </button>
    </form>
  );
}

function CodeForm({
  step,
  onRestart,
  onResent,
  onSignedIn,
}: {
  step: CodeStep;
  onRestart: () => void;
  onResent: (attempt: SignInAttempt) => void;
  onSignedIn: (account: Parameters<typeof afterSignIn>[0]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { attempt } = step;
  const [code, setCode] = useState('');
  const [problem, setProblem] = useState<{ message: string; restart?: boolean }>();
  const [pending, setPending] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const wait = secondsLeft(attempt, now);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const submit = async (value: string) => {
    setPending(true);
    setProblem(undefined);
    try {
      const result = await consumeCode({
        deviceId: attempt.deviceId,
        preAuthSessionId: attempt.preAuthSessionId,
        userInputCode: value,
        locale: currentLanguage(),
        ...timeZoneField(),
      });
      if (result.status === 'SIGNED_IN') {
        await onSignedIn(result.account);
        return;
      }
      setCode('');
      if (result.status === 'INCORRECT_CODE') {
        setProblem({ message: t('signIn.code.incorrect', { count: result.attemptsRemaining }) });
      } else if (result.status === 'EXPIRED_CODE') {
        setProblem({ message: t('signIn.code.expired') });
      } else {
        setProblem({ message: t('signIn.code.restart'), restart: true });
      }
    } catch (failure) {
      setCode('');
      const code = errorCode(failure, ['rate_limited', 'account_unavailable']);
      setProblem({ message: t(`signIn.errors.${code}`), restart: code === 'account_unavailable' });
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    setProblem(undefined);
    try {
      const sent = await resendCode({
        deviceId: attempt.deviceId,
        preAuthSessionId: attempt.preAuthSessionId,
        locale: currentLanguage(),
      });
      const renewed = {
        ...attempt,
        sentAt: Date.now(),
        resendAfterSeconds: sent.resendAfterSeconds,
      };
      saveAttempt(renewed);
      onResent(renewed);
      setCode('');
      setProblem(undefined);
    } catch (failure) {
      const code = errorCode(failure, ['rate_limited', 'restart_sign_in']);
      setProblem({ message: t(`signIn.errors.${code}`), restart: code === 'restart_sign_in' });
    }
  };

  const labelId = `${id}-label`;
  const messageId = `${id}-message`;
  const minutes = Math.round(attempt.codeLifetimeSeconds / 60);
  return (
    <div className="sign-in__form">
      <h2 className="sign-in__step-title">
        {attempt.method === 'EMAIL' ? t('signIn.code.titleEmail') : t('signIn.code.titlePhone')}
      </h2>
      <p>{t('signIn.code.sent', { contact: step.display, minutes })}</p>
      {attempt.method === 'EMAIL' ? (
        <p className="sign-in__fineprint">{t('signIn.code.link')}</p>
      ) : null}

      <p className="field__label" id={labelId}>
        {t('signIn.code.label')}
      </p>
      <CodeEntry
        value={code}
        onChange={setCode}
        onComplete={(value) => void submit(value)}
        labelledBy={labelId}
        {...(problem ? { describedBy: messageId } : {})}
        invalid={Boolean(problem)}
        disabled={pending || problem?.restart === true}
      />
      <p className="sign-in__status" aria-live="polite">
        {pending ? t('signIn.code.checking') : ''}
      </p>
      {problem ? (
        <p className="field__error" id={messageId} role="alert">
          {problem.message}
        </p>
      ) : null}

      <div className="sign-in__actions">
        {problem?.restart ? (
          <button className="btn btn--primary" type="button" onClick={onRestart}>
            {t('signIn.code.startAgain')}
          </button>
        ) : (
          <NoCodeHelp
            wait={wait}
            channel={attempt.method === 'SMS' ? 'sms' : 'email'}
            onResend={() => void resend()}
          />
        )}
        <button className="btn btn--quiet" type="button" onClick={onRestart}>
          {t('signIn.code.different')}
        </button>
      </div>
    </div>
  );
}

/** Seconds until "send a new code" is offered. */
function secondsLeft(attempt: SignInAttempt, now: number): number {
  return Math.max(
    0,
    Math.ceil((attempt.sentAt + attempt.resendAfterSeconds * 1_000 - now) / 1_000),
  );
}

/** The browser's time zone, as a request field when known. */
export function timeZoneField(): { timeZone?: string } {
  const timeZone = browserTimeZone();
  return timeZone ? { timeZone } : {};
}
